import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import sprout from "./assets/flower-1.png";
import leaves from "./assets/flower-2.png";
import newLeaves from "./assets/flower-3.png";
import buds from "./assets/flower-4.png";
import flower from "./assets/flower-5.png";
import pot from "./assets/pot.png";
import giftFish from "./assets/gift-fish.png";
import cls from "./styles.module.scss";
import { DeferredImage } from "@/ui/DeferredImage/DeferredImage";

// The real flower-1 catalog: cumulative days with active fireflies, not
// calendar age. Preserve each stage's scale and align its root with the pot.
const stages = [
  {
    image: sprout,
    title: "Первый росток",
    days: 0,
    height: 198,
    anchor: 0.47143,
  },
  {
    image: leaves,
    title: "Первые листья",
    days: 1,
    height: 296,
    anchor: 0.4575,
  },
  {
    image: newLeaves,
    title: "Больше зелени",
    days: 3,
    height: 441,
    anchor: 0.45668,
  },
  {
    image: buds,
    title: "Появились бутоны",
    days: 6,
    height: 573,
    anchor: 0.45659,
  },
  {
    image: flower,
    title: "Цветок распустился",
    days: 10,
    height: 637,
    anchor: 0.44653,
  },
];

function DemoFireflies({ lit }: { lit: boolean }) {
  return (
    <div
      className={`${cls.fireflies} ${lit ? cls.lit : ""}`}
      aria-hidden="true"
    >
      <span />
      <span />
      <span />
    </div>
  );
}

type GrowthPhase =
  | "idle"
  | "running"
  | "awaiting-light"
  | "resumed"
  | "finished";
const DEMO_DAY_MS = 1_000;
const DEMO_TICK_MS = 50;
const demoDays = Array.from({ length: 10 }, (_, index) => index + 1);

export function HomepageGrowth() {
  const [growth, setGrowth] = useState<{ days: number; phase: GrowthPhase }>({
    days: 0,
    phase: "idle",
  });
  const { days, phase } = growth;
  const stageIndex = stages.reduce(
    (current, item, index) => (days >= item.days ? index : current),
    0
  );
  const stage = stages[stageIndex];
  const active = phase === "running" || phase === "resumed";
  const lit = active || phase === "finished";

  useEffect(() => {
    if (phase !== "running" && phase !== "resumed") return;
    const limit = phase === "running" ? 5 : 10;
    let previousTick = performance.now();
    const timer = window.setInterval(() => {
      const now = performance.now();
      const delta = (now - previousTick) / DEMO_DAY_MS;
      previousTick = now;
      setGrowth((current) => {
        // Ignore stale ticks after reset, pause, or another run.
        if (current.phase !== phase) return current;
        const nextDays = Math.min(
          limit,
          Math.round((current.days + delta) * 1_000_000) / 1_000_000
        );
        return {
          days: nextDays,
          phase:
            nextDays < limit
              ? phase
              : limit === 5
              ? "awaiting-light"
              : "finished",
        };
      });
    }, DEMO_TICK_MS);
    return () => window.clearInterval(timer);
  }, [phase]);

  const buttonLabel =
    phase === "awaiting-light"
      ? "Зажечь ещё свет"
      : phase === "finished"
      ? "Цветок вырос"
      : active
      ? "Растёт"
      : "Зажечь свет";
  const status =
    phase === "awaiting-light"
      ? "Свет погас на пятом дне. Рост на паузе. Зажги ещё свет, чтобы продолжить."
      : phase === "finished"
      ? "Цветок распустился — десять дней накопленного света."
      : phase === "idle"
      ? "Ускоренный пример роста цветка. Зажги свет, чтобы увидеть все стадии. Настоящий свет не расходуется."
      : `${stage.title}. Рост идёт.`;

  return (
    <div
      className={`${cls.layout} ${cls.growth}`}
      data-home-growth
      data-demo-lit={lit}
      data-demo-phase={phase}
    >
      <div className={cls.demo} data-home-demo="growth">
        <div className={cls.window} data-demo-scene="growth">
          <div className={`${cls.copy} ${cls.sceneIntro}`}>
            <h2 id="growth-title">Цветы памяти</h2>
            <Link className={cls.textLink} to="/faq#faq-4">
              О свете и растениях <span aria-hidden="true">↗</span>
            </Link>
          </div>
          <DemoFireflies lit={lit} />
          <div className={cls.plantPlacement} data-demo-plant>
            <div className={cls.plantSpace}>
              <DeferredImage
                src={stage.image}
                alt={stage.title}
                className={cls.plant}
                style={{
                  height: `${(stage.height / 637) * 100}%`,
                  transform: `translateX(-${stage.anchor * 100}%)`,
                }}
                decoding="async"
              />
            </div>
            <DeferredImage
              src={pot}
              alt=""
              className={cls.pot}
              data-demo-pot
              decoding="async"
            />
          </div>
        </div>
        <div className={cls.demoBody}>
          <p id="growth-status" className={cls.visuallyHidden} role="status">
            {status}
          </p>
          <div className={cls.growthProgress}>
            <ol className={cls.stages} aria-label="Стадии роста: дни света">
              {stages.map((item, index) => (
                <li
                  key={item.days}
                  aria-current={index === stageIndex ? "step" : undefined}
                  className={days >= item.days ? cls.reached : undefined}
                  style={{ left: `${item.days * 10}%` }}
                >
                  <span className={cls.stageArtwork} aria-hidden="true">
                    <DeferredImage
                      src={item.image}
                      alt=""
                      style={{
                        height: `${(item.height / 637) * 100}%`,
                        transform: `translateX(-${item.anchor * 100}%)`,
                      }}
                      decoding="async"
                    />
                  </span>
                  <span className={cls.stageNumber}>{item.days}</span>
                  <span className={cls.visuallyHidden}>{item.title}</span>
                </li>
              ))}
            </ol>
            <div
              className={cls.progressTrack}
              role="progressbar"
              aria-labelledby="growth-days-label"
              aria-describedby="growth-status"
              aria-valuemin={0}
              aria-valuemax={10}
              aria-valuenow={Math.round(days * 100) / 100}
            >
              {demoDays.map((day) => (
                <span
                  key={day}
                  className={cls.progressDay}
                  data-demo-growth-day={day}
                  aria-hidden="true"
                >
                  <span
                    className={cls.progressFill}
                    style={{
                      transform: `scaleX(${Math.max(
                        0,
                        Math.min(1, days - day + 1)
                      )})`,
                    }}
                  />
                </span>
              ))}
            </div>
          </div>
          <p id="growth-days-label" className={cls.stageCaption}>
            Дни накопленного света
          </p>
          <div className={cls.controls}>
            <button
              type="button"
              className={cls.primaryButton}
              disabled={active || phase === "finished"}
              aria-describedby="growth-status"
              onClick={() =>
                setGrowth((current) =>
                  current.phase === "idle"
                    ? { days: 0, phase: "running" }
                    : current.phase === "awaiting-light"
                    ? { days: current.days, phase: "resumed" }
                    : current
                )
              }
            >
              {buttonLabel}
            </button>
          </div>
          <button
            type="button"
            className={cls.resetButton}
            onClick={() => setGrowth({ days: 0, phase: "idle" })}
          >
            Начать заново
          </button>
        </div>
      </div>
    </div>
  );
}

const warmthSteps = ["Светлячок", "Посвящение", "Одобрение", "Стикер"];
const warmthActions = [
  "Зажечь в примере",
  "Добавить посвящение с подарком",
  "Одобрить в примере",
  "Разместить подарок в примере",
  "Подарок в окне памяти",
];
const warmthStatuses = [
  "Начни с маленького огонька",
  "Светлячок зажжён. Можно добавить тёплые слова",
  "Посвящение с подарком ждёт одобрения владельца",
  "Посвящение одобрено. Подарок доступен владельцу как стикер",
  "Владелец разместил подарок в окне памяти",
];

export function HomepageWarmth() {
  const [step, setStep] = useState(0);

  return (
    <div
      className={`${cls.layout} ${cls.warmth}`}
      data-home-warmth
      data-demo-lit={step > 0}
    >
      <div className={cls.demo} data-home-demo="warmth">
        <div className={cls.window} data-demo-scene="warmth">
          <div className={`${cls.copy} ${cls.sceneIntro}`}>
            <h2 id="warmth-title">Передать тепло</h2>
            <Link className={cls.textLink} to="/faq#faq-5">
              О посвящениях и подарках <span aria-hidden="true">↗</span>
            </Link>
          </div>
          <DemoFireflies lit={step > 0} />
          <div className={cls.warmthVisual}>
            <span
              className={cls.warmthGlow}
              data-lit={step > 0}
              aria-hidden="true"
            />
            {step >= 2 && step < 4 && (
              <div className={cls.dedication}>
                <div>
                  <blockquote>
                    Я рядом. Пусть здесь останется немного тепла.
                  </blockquote>
                  <p className={cls.approval}>
                    {step === 2 ? "Ожидает одобрения" : "Одобрено владельцем"}
                  </p>
                </div>
                <DeferredImage
                  src={giftFish}
                  alt="Подарок к посвящению: рыбка"
                  decoding="async"
                />
              </div>
            )}
            {step === 4 && (
              <DeferredImage
                src={giftFish}
                alt="Подарок-рыбка размещён в окне памяти"
                className={cls.placedGift}
                decoding="async"
              />
            )}
          </div>
        </div>
        <div className={cls.demoBody}>
          <ol
            className={cls.warmthSteps}
            aria-label="Путь подарка в окно памяти"
          >
            {warmthSteps.map((title, index) => (
              <li
                key={title}
                className={index < step ? cls.reached : undefined}
                aria-current={index === step - 1 ? "step" : undefined}
              >
                <span aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                {title}
              </li>
            ))}
          </ol>
          <p id="warmth-status" className={cls.visuallyHidden} role="status">
            {warmthStatuses[step]}
          </p>
          <div className={cls.controls}>
            <button
              type="button"
              className={cls.primaryButton}
              disabled={step === 4}
              aria-describedby="warmth-status warmth-note"
              onClick={() => setStep((value) => Math.min(value + 1, 4))}
            >
              {warmthActions[step]}
            </button>
          </div>
          <p id="warmth-note" className={cls.visuallyHidden}>
            Это только пример: свет не тратится, посвящение никому не
            отправляется.
          </p>
          <button
            type="button"
            className={cls.resetButton}
            onClick={() => setStep(0)}
          >
            Посмотреть сначала
          </button>
        </div>
      </div>
    </div>
  );
}
